import { fetchSites, fetchThemesPlugins } from './apiService';
import { resolvePlanTierFromPlugins, rollupPlanTiers } from '../utils/pluginPlan';

const sitePluginsCache = new Map();
const sitePluginsInflight = new Map();

const MAX_PLUGIN_PAGES = 15;
const PLUGINS_PER_PAGE = 100;

export function invalidateSitePlugins(siteId) {
	if (!siteId) {
		return;
	}

	sitePluginsCache.delete(siteId);
	sitePluginsInflight.delete(siteId);
}

export async function fetchAllSitePlugins(siteId, { forceRefresh = false } = {}) {
	if (!siteId) {
		return [];
	}

	if (!forceRefresh && sitePluginsCache.has(siteId)) {
		return sitePluginsCache.get(siteId);
	}

	if (sitePluginsInflight.has(siteId)) {
		return sitePluginsInflight.get(siteId);
	}

	const promise = (async () => {
		const plugins = [];
		let page = 1;
		let total = 0;

		do {
			const response = await fetchThemesPlugins(siteId, page, PLUGINS_PER_PAGE);
			const batch = response.data || [];
			total = Number(response.total) || 0;
			plugins.push(...batch);

			if (batch.length === 0) {
				break;
			}

			page += 1;
		} while (plugins.length < total && page <= MAX_PLUGIN_PAGES);

		sitePluginsCache.set(siteId, plugins);
		return plugins;
	})();

	sitePluginsInflight.set(siteId, promise);

	try {
		return await promise;
	} finally {
		sitePluginsInflight.delete(siteId);
	}
}

export async function fetchSitePlanTier(siteId, freeSlug, proSlug) {
	if (!siteId || !freeSlug) {
		return null;
	}

	try {
		const plugins = await fetchAllSitePlugins(siteId);
		return resolvePlanTierFromPlugins(plugins, freeSlug, proSlug);
	} catch (error) {
		console.error('Error resolving site plan tier:', error);
		return null;
	}
}

export async function fetchPlanTiersForSites(sites, freeSlug, proSlug) {
	if (!sites?.length || !freeSlug) {
		return {};
	}

	const entries = await Promise.all(
		sites.map(async (site) => {
			const tier = await fetchSitePlanTier(site.id, freeSlug, proSlug);
			return [site.id, tier];
		})
	);

	return Object.fromEntries(entries);
}

export async function fetchUserPlanTier(userId, productId, freeSlug, proSlug) {
	if (!userId || !productId || !freeSlug) {
		return null;
	}

	try {
		const response = await fetchSites(productId, 1, 100, userId);
		const sites = response.data || [];

		if (sites.length === 0) {
			return null;
		}

		const tiers = await Promise.all(
			sites.map((site) => fetchSitePlanTier(site.id, freeSlug, proSlug))
		);

		return rollupPlanTiers(tiers.filter(Boolean));
	} catch (error) {
		console.error('Error resolving user plan tier:', error);
		return null;
	}
}

export async function fetchPlanTiersForUsers(users, productId, freeSlug, proSlug) {
	if (!users?.length || !productId || !freeSlug) {
		return {};
	}

	const entries = await Promise.all(
		users.map(async (user) => {
			const tier = await fetchUserPlanTier(user.id, productId, freeSlug, proSlug);
			return [user.id, tier];
		})
	);

	return Object.fromEntries(entries);
}

export function clearSitePlanCache() {
	sitePluginsCache.clear();
	sitePluginsInflight.clear();
}

const PLAN_SCAN_PER_PAGE = 50;
const PLAN_SCAN_MAX_PAGES = 200;

export async function fetchSitesByPlanFilter({
	productId,
	planFilter,
	freeSlug,
	proSlug,
	userId = null,
	searchTerm = '',
	stateFilter = '',
	uninstallReason = null,
	onlyProduction = false,
}) {
	if (!productId || !planFilter || !freeSlug) {
		return { data: [], total: 0 };
	}

	const matching = [];
	let page = 1;
	let apiTotal = 0;

	while (page <= PLAN_SCAN_MAX_PAGES) {
		const response = await fetchSites(
			productId,
			page,
			PLAN_SCAN_PER_PAGE,
			userId,
			searchTerm,
			stateFilter || null,
			uninstallReason,
			onlyProduction
		);

		const rows = response.data || [];
		apiTotal = Number(response.total) || 0;

		if (rows.length === 0) {
			break;
		}

		const planMap = await fetchPlanTiersForSites(rows, freeSlug, proSlug);

		rows.forEach((site) => {
			if (planMap[site.id] === planFilter) {
				matching.push(site);
			}
		});

		if (page * PLAN_SCAN_PER_PAGE >= apiTotal) {
			break;
		}

		page += 1;
	}

	return { data: matching, total: matching.length, apiTotal };
}
